import { useEffect, useState, useCallback } from "react"
import { UserCircleIcon, TrashIcon } from "@heroicons/react/24/solid"
import { EnkButton, EnkButtonBar, EnkPrimaryButton } from "../components/common/buttons"
import { EnkInputField } from "../components/inputs/inputfield"
import { EnkInput } from "../components/inputs/input"
import { EnkFieldGroup } from "../components/inputs/field-group"

import { EnkIWPServerAuth as ApiAuth, TEnkIWPUserProfile } from "../api/api-iwp-server"
import { useAuth } from "../auth/use-auth"
import EnkPageContainer from "../components/page-container"
import EnkContentLoadingAndError from "../components/error-box"

export default function EnkProfilePage() {
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState<boolean>(true)
  const [profile, setProfile] = useState<TEnkIWPUserProfile>({
    userId: 0,
    username: "",
    emailAddress: "",
    status: 10,
    userGroupId: 0,
    fullName: "",
    aboutMe: "",
    userKind: "Guest",
    mobileNumber: "",
    phoneNumber: "",
    profilePicture: "",
  })

  const auth = useAuth()

  useEffect(() => {
    // Fetch user profile data
    ApiAuth.getProfile()
      .then((fetchedProfile) => {
        setProfile(fetchedProfile)
        setLoading(false)
      })
      .catch((error) => {
        setError("Er is een fout opgetreden bij het ophalen van je profielgegevens.")
        console.error("Failed to fetch user profile data:", error)
      })
  }, [])

  const handleFotoUpload = () => {
    const input = document.createElement("input")
    input.type = "file"
    input.accept = "image/*"
    input.capture = "environment"
    input.onchange = (event) => {
      const file = (event.target as HTMLInputElement).files?.[0]
      if (file) {
        const reader = new FileReader()
        reader.onload = (e) => {
          const img = new Image()
          img.onload = () => {
            // Create a canvas to resize the image
            const canvas = document.createElement("canvas")
            const ctx = canvas.getContext("2d")

            // Set the desired thumbnail size
            const maxWidth = 100 // Thumbnail width
            const maxHeight = 100 // Thumbnail height
            let width = img.width
            let height = img.height

            // Calculate the new dimensions while maintaining the aspect ratio
            if (width > height) {
              if (width > maxWidth) {
                height = Math.round((height * maxWidth) / width) // Adjust height proportionally
                width = maxWidth
              }
            } else {
              if (height > maxHeight) {
                width = Math.round((width * maxHeight) / height) // Adjust width proportionally
                height = maxHeight
              }
            }

            // Set the canvas dimensions
            canvas.width = width
            canvas.height = height

            // Draw the resized image on the canvas
            ctx?.drawImage(img, 0, 0, width, height)

            // Get the resized image as a Base64 string
            const resizedBase64 = canvas.toDataURL("image/jpeg") // You can change the format if needed
            const newProfile = { ...profile, profilePicture: resizedBase64.split(",")[1] } // Remove the data URL prefix
            setProfile(newProfile)
          }

          img.src = e.target?.result as string // Load the original image into the <img> element
        }

        reader.readAsDataURL(file) // Read the file as a Base64 string
      }
    }
    input.click()
  }

  const handleDeleteProfilePicture = () => {
    setProfile((prevProfile) => ({ ...prevProfile, profilePicture: "" }))
  }

  const handleCommit = async () => {
    await ApiAuth.saveProfile(profile)
    let user = auth.user
    if (user) {
      user = {
        ...user,
        profilePicture: profile.profilePicture,
        username: profile.username || "",
        displayname: profile.fullName,
        email: profile.emailAddress || "",
      }
      auth.onUserInfoChanged(user)
    }
  }

  const handleInputChange = useCallback((event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { id, value } = event.target
    setProfile((prevProfile) => ({
      ...prevProfile,
      [id]: value,
    }))
  }, [])

  return (
    <EnkPageContainer>
      <EnkContentLoadingAndError loading={loading} error={error}>
        <form>
          <EnkFieldGroup
            label="Profiel"
            description="Deze informatie van je profiel is mogelijk zichtbaar voor andere gebruikers van de portal, wees voorzichtig met het delen van informatie."
          >
            <EnkInputField label="Gebruikersnaam" className="sm:col-span-4">
              <EnkInput
                id="username"
                type="text"
                placeholder="kies een gebruikersnaam"
                autoComplete="username"
                value={profile?.username || ""}
                onChange={handleInputChange}
              />
            </EnkInputField>
            <EnkInputField label="Over mij" className="col-span-full">
              <textarea
                id="aboutMe"
                rows={3}
                className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-primary sm:text-sm sm:leading-6"
                placeholder="Vertel iets over jezelf"
                value={profile?.aboutMe || ""}
                onChange={handleInputChange}
              />
            </EnkInputField>
            <EnkInputField label="Foto" className="col-span-full">
              <div className="mt-2 flex items-center gap-x-3">
                {profile?.profilePicture ? (
                  <>
                    <img
                      src={"data:image/jpeg;base64," + profile?.profilePicture}
                      alt="Profile photo"
                      className="h-12 w-12 rounded-full object-cover"
                    />
                    <EnkButton type="button" icon={TrashIcon} onClick={handleDeleteProfilePicture} />
                  </>
                ) : (
                  <UserCircleIcon id="photo" aria-hidden="true" className="h-12 w-12 text-gray-300" />
                )}

                <button
                  type="button"
                  className="rounded-md bg-white px-2.5 py-1.5 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                  onClick={handleFotoUpload}
                >
                  Upload Foto
                </button>
              </div>
            </EnkInputField>
          </EnkFieldGroup>

          <EnkFieldGroup label="Persoonlijke informatie" description="Deze informatie is alleen toegankelijk voor de beheerder van de portal.">
            <EnkInputField label="Volledige naam" className="sm:col-span-full">
              <EnkInput id="fullName" type="text" autoComplete="given-name" value={profile?.fullName || ""} onChange={handleInputChange} />
            </EnkInputField>

            <EnkInputField label="E-mail adres" className="col-span-full">
              <EnkInput id="emailAddress" type="email" autoComplete="email" value={profile?.emailAddress || ""} onChange={handleInputChange} />
            </EnkInputField>

            <EnkInputField label="Mobiele nummer" className="sm:col-span-2">
              <EnkInput id="mobileNumber" type="tel" autoComplete="tel" value={profile?.mobileNumber || ""} onChange={handleInputChange} />
            </EnkInputField>
            <EnkInputField label="Vast telefoon" className="sm:col-span-2">
              <EnkInput id="phoneNumber" type="tel" autoComplete="tel" value={profile?.phoneNumber || ""} onChange={handleInputChange} />
            </EnkInputField>
          </EnkFieldGroup>

          <EnkButtonBar>
            <EnkButton text="Annuleren" />
            <EnkPrimaryButton text="Opslaan" onClick={() => void handleCommit()} />
          </EnkButtonBar>
        </form>
      </EnkContentLoadingAndError>
    </EnkPageContainer>
  )
}
